import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  bulkCreateUrls,
  createUrl,
  deleteUrl,
  getUrl,
  listUrls,
  listUrlsPage,
  runUrl,
  updateUrl,
  type CheckRun,
  type Paginated,
  type UrlEntry,
  type UrlEntryBulkCreate,
  type UrlEntryBulkResult,
  type UrlEntryCreate,
  type UrlEntryUpdate,
  type UrlsListParams,
} from "@/lib/api";

export const urlsKeys = {
  all: ["urls"] as const,
  list: (siteId: number, params?: UrlsListParams) =>
    [...urlsKeys.all, "list", siteId, params] as const,
  detail: (id: number) => [...urlsKeys.all, "detail", id] as const,
};

export function useUrls(
  siteId: number | null,
  params?: UrlsListParams,
): UseQueryResult<UrlEntry[], Error> {
  return useQuery({
    queryKey: urlsKeys.list(siteId ?? 0, params),
    queryFn: () => listUrls(siteId as number, params),
    enabled: siteId !== null && siteId !== undefined && siteId > 0,
  });
}

export function useUrlsPage(
  siteId: number | null,
  params?: UrlsListParams,
): UseQueryResult<Paginated<UrlEntry>, Error> {
  return useQuery({
    queryKey: urlsKeys.list(siteId ?? 0, params),
    queryFn: () => listUrlsPage(siteId as number, params),
    enabled: siteId !== null && siteId !== undefined && siteId > 0,
    // Keep the current page visible while the next one loads (no flash).
    placeholderData: keepPreviousData,
  });
}

export function useUrl(id: number | null): UseQueryResult<UrlEntry, Error> {
  return useQuery({
    queryKey: urlsKeys.detail(id ?? 0),
    queryFn: () => getUrl(id as number),
    enabled: id !== null && id !== undefined && id > 0,
  });
}

export function useCreateUrl(): UseMutationResult<
  UrlEntry,
  Error,
  { siteId: number; body: UrlEntryCreate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, body }) => createUrl(siteId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: urlsKeys.all });
    },
  });
}

export function useBulkCreateUrls(): UseMutationResult<
  UrlEntryBulkResult,
  Error,
  { siteId: number; body: UrlEntryBulkCreate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, body }) => bulkCreateUrls(siteId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: urlsKeys.all });
    },
  });
}

export function useUpdateUrl(): UseMutationResult<
  UrlEntry,
  Error,
  { id: number; body: UrlEntryUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateUrl(id, body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: urlsKeys.all });
      qc.invalidateQueries({ queryKey: urlsKeys.detail(vars.id) });
    },
  });
}

export function useDeleteUrl(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteUrl(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: urlsKeys.all });
    },
  });
}

export function useRunUrl(): UseMutationResult<CheckRun, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => runUrl(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: urlsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}
